import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Search, X } from 'lucide-react';
import type { ChartDataset } from 'chart.js';
import { ChartBox } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { OpdRawResponse, OpdRawTopicRow, Tone } from '../types';
import { exportCsv, num } from '../utils';

const RANGE_OPTIONS = [
  { value: '7d', label: '7 ngày' },
  { value: '30d', label: '30 ngày' },
  { value: '90d', label: '90 ngày' },
  { value: 'all', label: 'Tất cả' },
];

const ACTION_LABEL: Record<string, string> = {
  accepted: 'Đã ghi',
  duplicate: 'Trùng, bỏ qua',
  rejected: 'Từ chối',
  error: 'Lỗi',
};
const ACTION_TONE: Record<string, Tone> = {
  accepted: 'green',
  duplicate: 'gray',
  rejected: 'amber',
  error: 'red',
};
const TYPE_LABEL: Record<string, string> = {
  text: 'Văn bản',
  post: 'Bài viết',
  image: 'Ảnh',
  file: 'Tệp',
  audio: 'Ghi âm',
  media: 'Video',
  sticker: 'Sticker',
  interactive: 'Thẻ',
  share_chat: 'Chia sẻ nhóm',
};

const typeLabel = (value: string) => TYPE_LABEL[value] || value || 'Khác';

interface Props {
  data: OpdRawResponse | null;
  loaded: boolean;
  busy: boolean;
  range: string;
  onRange: (range: string) => void;
}

export function OpdRawScreen({ data, loaded, busy, range, onRange }: Props) {
  // Hook phải nằm TRƯỚC mọi early-return (React #310).
  const [search, setSearch] = useState('');
  const [chatFilter, setChatFilter] = useState<string[]>([]);
  const [openTopics, setOpenTopics] = useState<string[]>([]);

  const rows = data?.rows || [];
  const kpi = data?.kpi || {};
  const daily = data?.daily || [];
  const chats = data?.chats || [];
  const people = data?.people || [];
  const types = data?.types || [];
  const ingest = data?.ingest || [];

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter(row => {
      if (chatFilter.length && !chatFilter.includes(row.chat_id)) return false;
      if (!needle) return true;
      const haystack = [
        row.topic_id,
        row.title,
        row.chat_name,
        row.transcript,
        (row.participants || []).join(' '),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [rows, search, chatFilter]);

  const filteredMessages = useMemo(
    () => filtered.reduce((sum, row) => sum + (row.message_count || 0), 0),
    [filtered],
  );

  const failures = useMemo(
    () => ingest.filter(row => row.action === 'error' || row.action === 'rejected')
      .reduce((sum, row) => sum + (row.n || 0), 0),
    [ingest],
  );

  const toggleChat = (chatId: string) => {
    setChatFilter(prev => prev.includes(chatId) ? prev.filter(id => id !== chatId) : [...prev, chatId]);
  };
  const toggleTopic = (topicId: string) => {
    setOpenTopics(prev => prev.includes(topicId) ? prev.filter(id => id !== topicId) : [...prev, topicId]);
  };
  const clearFilters = () => { setSearch(''); setChatFilter([]); };
  const hasFilter = Boolean(search.trim()) || chatFilter.length > 0;
  const chatLabel = (chatId: string) =>
    chats.find(row => row.chat_id === chatId)?.chat_name || chatId;

  if (!loaded && busy) return <div className="empty">Đang tải nhật ký vận hành…</div>;
  if (data?.error) return <div className="empty">Không tải được: {data.error}</div>;
  if (!loaded) return <div className="empty">Chưa có dữ liệu.</div>;

  return (
    <>
      <section className="filters">
        <div className="section-title">
          <div>
            <h2>Nhật ký thảo luận vận hành</h2>
            <span>Nguyên văn tin nhắn Lark, gom theo chủ đề — chưa phân loại, chưa diễn giải</span>
          </div>
          <div className="seg" aria-label="Khoảng thời gian">
            {RANGE_OPTIONS.map(option => (
              <button
                key={option.value}
                className={`tab ${range === option.value ? 'active' : ''}`}
                disabled={busy}
                onClick={() => onRange(option.value)}
                type="button"
              >{option.label}</button>
            ))}
          </div>
        </div>
        <div className="filter-grid opd-filter-grid">
          <div className="control">
            <label htmlFor="opdraw-search">Tìm kiếm</label>
            <div className="opd-search">
              <Search size={14} />
              <input
                id="opdraw-search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Nội dung, tên người, mã chủ đề..."
              />
            </div>
          </div>
        </div>
        <div className="filter-pills">
          {search && (
            <button className="pill filter" onClick={() => setSearch('')} type="button">
              Tìm: {search}<X size={12} />
            </button>
          )}
          {chatFilter.map(chatId => (
            <button key={chatId} className="pill filter" onClick={() => toggleChat(chatId)} type="button">
              {chatLabel(chatId)}<X size={12} />
            </button>
          ))}
          {hasFilter
            ? <button className="pill ghost" onClick={clearFilters} type="button">Xoá bộ lọc</button>
            : <span className="muted">Đang xem toàn bộ {num(rows.length)} chủ đề trong kỳ.</span>}
          <button className="pill ghost" onClick={() => exportCsv(filtered, undefined, 'OPD_nhat_ky_van_hanh')} type="button">
            <Download size={12} /> Xuất CSV
          </button>
        </div>
      </section>

      <section className="grid kpis">
        <KpiCard label="Chủ đề" value={num(kpi.topics)} sub={`${num(filtered.length)} sau lọc`} />
        <KpiCard label="Tin nhắn ghi nhận" value={num(kpi.messages)} sub={`${num(filteredMessages)} sau lọc`} tone="blue" />
        <KpiCard label="Group đang thu" value={num(kpi.chats)} sub={`${num(chats.length)} group đã biết`} tone="violet" />
        <KpiCard label="Người tham gia" value={num(kpi.people)} sub="Có tên trong kỳ" tone="cyan" />
        <KpiCard label="TB tin/chủ đề" value={String(kpi.avg_messages ?? 0)} sub={`Kéo dài TB ${num(kpi.avg_minutes)} phút`} />
        <KpiCard
          label="Ghi hụt 24h"
          value={num(failures)}
          sub={failures > 0 ? 'Xem bảng nhật ký ghi bên dưới' : 'Không có tin nào bị từ chối'}
          tone={failures > 0 ? 'red' : 'green'}
        />
      </section>

      {(kpi.unresolved_people || 0) > 0 && (
        <section className="card space">
          <div className="kpi-sub">
            Còn <b>{num(kpi.unresolved_people)}</b> người chưa tra được tên thật (mới xuất hiện, hoặc app
            Lark thiếu quyền <code>contact:user.base:readonly</code>). Transcript tạm hiện <code>open_id</code>;
            tra được tên là mọi transcript cũ tự sáng lên, không phải chạy lại gì.
          </div>
        </section>
      )}

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Nhịp thảo luận theo ngày</h2><span>Chủ đề và số tin</span></div>
          <div className="chart tall"><ChartBox
            labels={daily.map(row => row.day.slice(5))}
            datasets={[
              { label: 'Chủ đề', data: daily.map(row => row.topics), backgroundColor: '#2563eb', borderWidth: 0 },
              { label: 'Tin nhắn', data: daily.map(row => row.messages), type: 'line', borderColor: '#d71920', backgroundColor: '#d71920', yAxisID: 'y1', tension: 0.3 },
            ] as unknown as ChartDataset[]}
            options={{
              scales: {
                x: { grid: { display: false } },
                y: { beginAtZero: true, title: { display: true, text: 'Chủ đề' } },
                y1: { beginAtZero: true, position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Tin nhắn' } },
              },
            }}
          /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Ai nói nhiều nhất</h2><span>Theo số tin trong kỳ</span></div>
          <div className="chart" style={{ height: Math.max(220, people.length * 34 + 70) }}><ChartBox
            labels={people.map(row => row.name)}
            datasets={[{ label: 'Tin nhắn', data: people.map(row => row.messages), backgroundColor: '#0891b2', borderWidth: 0 }] as unknown as ChartDataset[]}
            options={{ indexAxis: 'y', plugins: { legend: { display: false } } }}
          /></div>
        </div>
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Group đang thu thập</h2><span>Bấm để lọc</span></div>
          <div className="scroll"><table>
            <thead><tr><th>Group</th><th className="right">Chủ đề</th><th className="right">Tin</th><th>Trạng thái</th><th>Gần nhất</th></tr></thead>
            <tbody>
              {chats.map(row => (
                <tr
                  key={row.chat_id}
                  onClick={() => toggleChat(row.chat_id)}
                  style={{ cursor: 'pointer', opacity: chatFilter.length && !chatFilter.includes(row.chat_id) ? 0.45 : 1 }}
                >
                  <td><b>{row.chat_name || row.chat_id}</b>{!row.chat_name && <><br /><span className="muted">chưa tra được tên</span></>}</td>
                  <td className="right tnum">{num(row.topics)}</td>
                  <td className="right tnum">{num(row.messages)}</td>
                  <td>{row.is_allowed ? <Tag tone="green">Đang thu</Tag> : <Tag tone="gray">Đã tắt</Tag>}</td>
                  <td className="muted">{row.last_seen || '—'}</td>
                </tr>
              ))}
              {chats.length === 0 && <tr><td colSpan={5} className="empty">Bot chưa được mời vào group nào.</td></tr>}
            </tbody>
          </table></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Nhật ký ghi</h2><span>24 giờ gần nhất</span></div>
          <div className="scroll"><table>
            <thead><tr><th>Kết quả</th><th className="right">Số lượt</th><th>Gần nhất</th></tr></thead>
            <tbody>
              {ingest.map(row => (
                <tr key={row.action}>
                  <td><Tag tone={ACTION_TONE[row.action] || 'gray'}>{ACTION_LABEL[row.action] || row.action}</Tag></td>
                  <td className="right tnum"><b>{num(row.n)}</b></td>
                  <td className="muted">{row.latest || '—'}</td>
                </tr>
              ))}
              {ingest.length === 0 && <tr><td colSpan={3} className="empty">Chưa có lượt ghi nào trong 24h.</td></tr>}
            </tbody>
          </table></div>
          <div className="section-title" style={{ marginTop: 16 }}><h2>Loại tin</h2><span>Trong kỳ</span></div>
          <div className="scroll"><table>
            <thead><tr><th>Loại</th><th className="right">Số tin</th></tr></thead>
            <tbody>
              {types.map(row => (
                <tr key={row.msg_type}><td>{typeLabel(row.msg_type)}</td><td className="right tnum">{num(row.messages)}</td></tr>
              ))}
              {types.length === 0 && <tr><td colSpan={2} className="empty">Chưa có tin nào.</td></tr>}
            </tbody>
          </table></div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Chủ đề và nguyên văn</h2>
          <span>{num(filtered.length)} chủ đề · bấm một dòng để mở transcript</span>
        </div>
        <div className="scroll">
          <table>
            <thead>
              <tr><th style={{ width: 32 }} /><th>Chủ đề</th><th>Group</th><th className="right">Tin</th><th className="right">Phút</th><th>Người tham gia</th><th>Thời gian</th></tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const open = openTopics.includes(row.topic_id);
                return [
                  <tr key={row.topic_id} onClick={() => toggleTopic(row.topic_id)} style={{ cursor: 'pointer' }}>
                    <td>{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                    <td><b>{row.title || row.topic_id}</b>{row.title && <><br /><span className="muted">{row.topic_id}</span></>}</td>
                    <td>{row.chat_name || row.chat_id}</td>
                    <td className="right tnum">{num(row.message_count)}</td>
                    <td className="right tnum">{num(row.duration_minutes)}</td>
                    <td>{(row.participants || []).join(', ') || '—'}</td>
                    <td className="muted">{row.opened_at} → {row.last_event_at}</td>
                  </tr>,
                  open ? (
                    <tr key={`${row.topic_id}-body`}>
                      <td colSpan={7}>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0, font: 'inherit', lineHeight: 1.6 }}>
                          {row.transcript || 'Chủ đề này không có nội dung văn bản.'}
                        </pre>
                        {row.transcript_truncated && (
                          <div className="muted" style={{ marginTop: 8 }}>
                            Transcript đã cắt bớt để tải nhanh. Đọc đầy đủ bằng cách hỏi agent <b>opd-tv</b> theo mã {row.topic_id}.
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="empty">
                  {rows.length === 0 ? 'Chưa có thảo luận nào được ghi nhận trong kỳ.' : 'Không có chủ đề khớp bộ lọc.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {data?.meta?.truncated && (
          <div className="muted" style={{ marginTop: 10 }}>
            Hiển thị {num(data.meta.returned)} / {num(data.meta.total)} chủ đề mới nhất. Thu hẹp khoảng thời gian để xem phần còn lại.
          </div>
        )}
      </section>
    </>
  );
}
